import React from 'react';
import OpenInViewDropdown from '../../../OpenInViewDropdown';

const CLASS_PREFIX = 'mission-card';

/** True when a rich-text doc field holds anything beyond empty markup/whitespace. */
const hasRichTextContent = (html) => {
  if (!html || typeof html !== 'string') return false;
  return html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().length > 0;
};

/**
 * CardToolbar Component
 * 
 * Renders the toolbar at the bottom of the card with:
 * - Open Mission button (primary action, left)
 * - Attachments button
 * - Open in View dropdown
 * - Archive/Restore button
 * - Delete button (destructive, right)
 */
const CardToolbar = ({
  task,
  // Campaign ID for URL navigation
  campaignId,
  // Visibility flags
  showAttachmentsButton = true,
  showArchiveButton = false,
  showDeleteButton = false,
  showOpenInViewButton = false,
  showViewMissionButton = false,
  // Counts
  attachmentsCount = 0,
  // Callbacks
  onOpenAttachments,
  onArchiveTask,
  onUnarchiveTask,
  onDeleteTask,
  onOpenDetails,
  // View navigation (for "Open in view" button)
  currentView,
  customViews = [],
  activeCustomViewId = null,
}) => {
  // Calculate stats
  const hasAttachments = attachmentsCount > 0;
  
  // Deliverables count shown on Open Mission button
  const deliverables = task.deliverables || [];
  const totalDeliverables = deliverables.length;
  const completedDeliverables = deliverables.filter(d => d.completed).length;
  const hasDeliverables = totalDeliverables > 0;

  // Whether the mission doc (rich-text body) has any authored content — drives the
  // Open Mission icon so an empty vs. written doc is distinguishable at a glance.
  const hasDocsContent = hasRichTextContent(task.notes) || hasRichTextContent(task.internalNotes);

  const showToolbar = showAttachmentsButton || showArchiveButton || showDeleteButton || showOpenInViewButton || showViewMissionButton;
  
  if (!showToolbar) return null;

  return (
    <div className={`${CLASS_PREFIX}__toolbar`}>
      {/* Open Mission — primary action, anchored left */}
      {showViewMissionButton && (
        <button
          className={`${CLASS_PREFIX}__toolbar-item ${CLASS_PREFIX}__toolbar-item--view-mission ${hasDocsContent ? `${CLASS_PREFIX}__toolbar-item--has-docs` : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenDetails?.(task.id);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          title={hasDocsContent ? 'Open Mission · contains notes' : 'Open Mission'}
        >
          {hasDocsContent ? (
            /* Page with text lines — doc has authored content */
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="16" y1="13" x2="8" y2="13" />
              <line x1="16" y1="17" x2="8" y2="17" />
              <polyline points="10 9 9 9 8 9" />
            </svg>
          ) : (
            /* Blank page — doc is empty */
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
            </svg>
          )}
          <span>Open Mission{hasDeliverables && ` · ${completedDeliverables}/${totalDeliverables}`}</span>
          <svg className={`${CLASS_PREFIX}__toolbar-item-arrow`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
      )}

      {/* Spacer pushes secondary icon actions to the right edge */}
      <span className={`${CLASS_PREFIX}__toolbar-spacer`} aria-hidden="true" />

      {/* Secondary icon cluster */}
      <div className={`${CLASS_PREFIX}__toolbar-group`}>
      {/* Attachments */}
      {showAttachmentsButton && (
        <button
          className={`${CLASS_PREFIX}__toolbar-item ${CLASS_PREFIX}__toolbar-item--attachments ${hasAttachments ? `${CLASS_PREFIX}__toolbar-item--has-count` : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onOpenAttachments?.(task.id);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          title={hasAttachments ? `${attachmentsCount} attachment${attachmentsCount !== 1 ? 's' : ''}` : "Add attachments"}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
          </svg>
          {hasAttachments && (
            <span className={`${CLASS_PREFIX}__toolbar-count`}>{attachmentsCount}</span>
          )}
        </button>
      )}

      {/* Open in View */}
      {showOpenInViewButton && (
        <OpenInViewDropdown
          taskId={task.id}
          campaignId={campaignId}
          currentView={currentView}
          customViews={customViews}
          activeCustomViewId={activeCustomViewId}
          variant="icon"
          className={`${CLASS_PREFIX}__toolbar-item`}
        />
      )}

      {/* Archive/Unarchive */}
      {showArchiveButton && (
        task.archived ? (
          <button
            className={`${CLASS_PREFIX}__toolbar-item ${CLASS_PREFIX}__toolbar-item--restore`}
            onClick={(e) => {
              e.stopPropagation();
              onUnarchiveTask?.(task.id);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            title="Restore from archive"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 8 3 21 21 21 21 8" />
              <rect x="1" y="3" width="22" height="5" />
              <polyline points="10 12 14 16 10 20" />
              <line x1="14" y1="16" x2="6" y2="16" />
            </svg>
          </button>
        ) : (
          <button
            className={`${CLASS_PREFIX}__toolbar-item ${CLASS_PREFIX}__toolbar-item--archive`}
            onClick={(e) => {
              e.stopPropagation();
              onArchiveTask?.(task.id);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            title="Archive this task"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="21 8 21 21 3 21 3 8" />
              <rect x="1" y="3" width="22" height="5" />
              <line x1="10" y1="12" x2="14" y2="12" />
            </svg>
          </button>
        )
      )}

      {/* Delete - Destructive action, positioned last */}
      {showDeleteButton && (
        <button
          className={`${CLASS_PREFIX}__toolbar-item ${CLASS_PREFIX}__toolbar-item--delete`}
          onClick={(e) => {
            e.stopPropagation();
            onDeleteTask?.(task.id);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          title="Delete this mission"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            <line x1="10" y1="11" x2="10" y2="17" />
            <line x1="14" y1="11" x2="14" y2="17" />
          </svg>
        </button>
      )}
      </div>
    </div>
  );
};

export default CardToolbar;
